import { useState } from 'react'

import { Modal, useModals } from 'almanac-ui/Modal'

import { useUpdateAutoReloadCredits } from 'hooks/useUpdateAutoReloadCredits'

import Styles from './AutoReloadModal.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Check02 } from 'blaze-ui/icons/16'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { InformationCircleContained } from 'blaze-ui/icons'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Minus } from 'almanac-ui/icons'
import { Plus1 } from 'blaze-ui/icons/24'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Pressable } from '@react-aria/interactions'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

type AutoReloadLimit = number | null | undefined

export const AutoReloadModal = () => {
  const updateAutoReloadCredits = useUpdateAutoReloadCredits()
  const workspace = useCurrentWorkspace()
  const [reloadBatches, setReloadBatches] = useState<AutoReloadLimit>(() => {
    if (!workspace?.auto_reload_credits) return 5
    if (workspace.auto_reload_limit === null || workspace.auto_reload_limit === undefined) return null
    return workspace.auto_reload_limit / 100
  })
  const { closeModal } = useModals()

  const onConfirm = (autoReloadCredits: boolean, autoReloadLimit: AutoReloadLimit) => {
    updateAutoReloadCredits.mutate(
      { autoReloadCredits, autoReloadLimit: autoReloadLimit || null },
      {
        onSuccess: () => {
          closeModal()
          if (autoReloadCredits) {
            showToastMessage('Auto-reload credits enabled successfully')
          } else {
            showToastMessage('Auto-reload credits disabled successfully')
          }
        },
        onError: () => {
          showToastMessage('Unable to update auto-reload settings', 'error')
        },
      },
    )
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header title={workspace?.auto_reload_credits ? 'Manage auto-reload' : 'Set up auto-reload'} />
      <Modal.Content>
        <div className={Styles.content}>
          <Text variant="secondary" color="var(--dark-60)">
            We'll automatically add credits when your balance is low, up to the monthly limit you set, so your content
            keeps generating without interruption.
          </Text>
          <div className={Styles.featureControl}>
            <div className={Styles.descriptionAndControl}>
              <div className={Styles.description}>
                <Text variant="largeList">Monthly auto-reload budget</Text>
                <Tooltip
                  placement="bottom"
                  label="The maximum amount you're willing to spend on auto-reload each month."
                  hide={false}
                >
                  <div className={Styles.infoIcon}>
                    <InformationCircleContained size={16} color="var(--dark-40)" />
                  </div>
                </Tooltip>
              </div>
              {!!reloadBatches && (
                <div className={Styles.controlContainer}>
                  <Text variant="secondary" color="var(--dark-40)">
                    {reloadBatches * 100} credits
                  </Text>
                  <div className={Styles.control}>
                    <IconButton
                      icon={Minus}
                      onPress={() => setReloadBatches(Math.max(reloadBatches - 1, 1))}
                      variant="ghost-tertiary"
                      size="xs"
                    />
                    <div className={Styles.count}>
                      <Heading level={5} color="var(--dark-80)">
                        ${reloadBatches * 10}
                      </Heading>
                    </div>
                    <IconButton
                      icon={Plus1}
                      onPress={() => setReloadBatches(reloadBatches + 1)}
                      variant="ghost-tertiary"
                      size="xs"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={Styles.removeLimitContainer}>
              {!!reloadBatches ? (
                <Pressable onPress={() => setReloadBatches(null)}>
                  <div className={Styles.removeLimit}>
                    <Text variant="secondary" color="var(--selected-fill)">
                      Remove Limit
                    </Text>
                  </div>
                </Pressable>
              ) : (
                <>
                  <Text variant="secondary" color="var(--dark-40)">
                    No limit. Auto-reload will only add credits when needed.
                  </Text>
                  <Pressable onPress={() => setReloadBatches(5)}>
                    <div className={Styles.removeLimit}>
                      <Text variant="secondary" color="var(--selected-fill)">
                        Set Limit
                      </Text>
                    </div>
                  </Pressable>
                </>
              )}
            </div>
          </div>
          <div className={Styles.explanation}>
            <div className={Styles.checklist}>
              <div className={Styles.checklistItem}>
                <Check02 color="var(--dark-60)" />
                <Text variant="secondary">Keep content generating without interruption</Text>
              </div>
              <div className={Styles.checklistItem}>
                <Check02 color="var(--dark-60)" />
                <Text variant="secondary">Change or turn this off anytime</Text>
              </div>
              <div className={Styles.checklistItem}>
                <Check02 color="var(--dark-60)" />
                <Text variant="secondary">Credits expire every 30 days</Text>
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {workspace?.auto_reload_credits ? (
            <div className={Styles.rightFooterButtons}>
              <Modal.FooterButton variant="secondary" onPress={() => onConfirm(false, null)}>
                Disable Auto-reload
              </Modal.FooterButton>
              <Modal.FooterButton onPress={() => onConfirm(true, reloadBatches ? reloadBatches * 100 : null)}>
                Save Changes
              </Modal.FooterButton>
            </div>
          ) : (
            <Modal.FooterButton onPress={() => onConfirm(true, reloadBatches ? reloadBatches * 100 : null)}>
              Enable Auto-Reload
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
